import { reactive } from 'vue'

/**
 * Supervision (ver extras_mg/supervision/ en el backend).
 *
 * Solo Victor e Isaac supervisan. El navegador NO decide nada de eso: pregunta
 * al servidor (/supervision/yo/) y, aunque alguien cambiara este estado a mano
 * en su consola, el servidor le seguiria negando el panel y no le daria ningun
 * espacio de mas.
 *
 * El estado vive aqui, fuera de Vuex, porque lo leen piezas sueltas (el aviso
 * del layout, el menu de usuario, la pagina y el interceptor de axios) y es
 * tan chico que no merece un modulo de store.
 */
export const supervision = reactive({
  soy: false,
  // De quien es la respuesta: al cerrar sesion el core no recarga la pagina, y
  // quien entra despues en la misma pestana no debe heredar lo del anterior.
  usuario: null,
  // La confirmacion que espera respuesta: { datos, resolver }.
  pedido: null,
})

export function servicio(client) {
  return {
    yo() {
      return client.get('/extras-mg/supervision/yo/')
    },
    panorama() {
      return client.get('/extras-mg/supervision/')
    },
    sincronizar() {
      return client.post('/extras-mg/supervision/sincronizar/')
    },
  }
}

export async function cargar(client, usuario) {
  if (!usuario) {
    supervision.soy = false
    supervision.usuario = null
    return false
  }
  if (supervision.usuario === usuario) {
    return supervision.soy
  }
  supervision.soy = false
  supervision.usuario = usuario
  try {
    const { data } = await servicio(client).yo()
    if (supervision.usuario === usuario) {
      supervision.soy = Boolean(data.supervisor)
    }
  } catch (e) {
    // Que lo vuelva a preguntar la proxima vez, en vez de quedarse en "no".
    if (supervision.usuario === usuario) {
      supervision.usuario = null
    }
  }
  return supervision.soy
}

/**
 * Pide al modal de confirmacion que pregunte. Resuelve con la frase escrita, o
 * con null si la persona cancelo. Si ya habia una pregunta abierta, esa se da
 * por cancelada: nunca se confirma algo que no se esta viendo.
 */
export function pedirConfirmacion(datos) {
  if (supervision.pedido) {
    supervision.pedido.resolver(null)
  }
  return new Promise((resolve) => {
    supervision.pedido = {
      datos,
      resolver: (frase) => {
        supervision.pedido = null
        resolve(frase)
      },
    }
  })
}

const CABECERA_TOKEN = 'X-MG-Confirmacion'
const CABECERA_FRASE = 'X-MG-Frase'

function ponerCabecera(config, nombre, valor) {
  if (config.headers && typeof config.headers.set === 'function') {
    config.headers.set(nombre, valor)
  } else {
    config.headers = { ...(config.headers || {}), [nombre]: valor }
  }
}

/**
 * Interceptor de respuestas: cuando el servidor contesta que un cambio pide
 * confirmacion (428, ERROR_MG_CONFIRMAR), se ensena el modal y, si la persona
 * confirma, se repite LA MISMA peticion con el token y la frase. Para quien
 * llamo es como si la primera hubiera salido bien (o mal, si cancelo).
 *
 * Al cancelar se rechaza con el error original rebautizado ERROR_MG_CANCELADO,
 * que el mapa de errores traduce a "Cambio cancelado. No se cambió nada.": las
 * pantallas del core ya saben deshacer su cambio optimista y avisar.
 */
export function instalarInterceptor(nuxtApp) {
  const { $client, $clientErrorMap } = nuxtApp
  $clientErrorMap.setError(
    'ERROR_MG_CANCELADO',
    'Cambio cancelado',
    'No se cambió nada.'
  )
  $clientErrorMap.setError(
    'ERROR_MG_CONFIRMAR',
    'Hace falta confirmar',
    'Este cambio pide confirmación porque supervisas este espacio de trabajo. Recarga la página e inténtalo de nuevo.'
  )

  $client.interceptors.response.use(null, async (error) => {
    const respuesta = error?.response
    if (
      respuesta?.status !== 428 ||
      respuesta?.data?.error !== 'ERROR_MG_CONFIRMAR' ||
      !respuesta.data.confirmacion ||
      error.config?.__mgConfirmado
    ) {
      throw error
    }
    const datos = respuesta.data.confirmacion
    const frase = await pedirConfirmacion(datos)
    if (!frase) {
      if (error.handler) {
        error.handler.setError('ERROR_MG_CANCELADO', 'No se cambió nada.')
      }
      throw error
    }
    const config = { ...error.config, __mgConfirmado: true }
    ponerCabecera(config, CABECERA_TOKEN, datos.token)
    ponerCabecera(config, CABECERA_FRASE, frase)
    return $client.request(config)
  })
}
